import classNames from 'classnames'
import { CSSProperties, useCallback, useMemo, useState } from 'react'
import { useEffect } from 'react'
import { Transforms } from 'slate'
import { ReactEditor, RenderElementProps, useSlateStatic } from 'slate-react'

import { Input } from 'core-components/library/inputs'
import { ModalDialog } from 'core-components/library/modalDialog'
import { CLOUDINARY_ZOOM_URL } from 'core-components/library/richTextEditor/constants'
import { getImageSettings } from 'core-components/library/richTextEditor/helpers/imageHelper'
import {
  updateCaptionUnderImage,
  updateImage,
} from 'core-components/library/richTextEditor/helpers/slateHelper'
import { TImageElement } from 'core-components/library/richTextEditor/interface/custom-types'
import { TImageSettings } from 'core-components/library/richTextEditor/interface/TImage'
import { ImageSettingsDialog } from 'core-components/library/richTextEditor/toolbar/components/image/ImageSettingsDialog'

import { TopPanelButtonsWrapper } from '../topPanelButtonsWrapper/TopPanelButtonsWrapper'

import styles from './Image.module.scss'

interface IProps {
  style: CSSProperties
}

export const Image = (props: RenderElementProps & IProps) => {
  const [imageViewDialogVisible, setImageViewDialogVisible] = useState<boolean>(false)
  const [imageSettingsDialogVisible, setImageSettingsDialogVisible] = useState<boolean>(false)
  const [isInputCaption, setIsInputCaption] = useState<boolean>(false)

  const element = useMemo(() => props.element as TImageElement, [props.element])

  const [captionValue, setCaptionValue] = useState<string>(element.caption || '')

  const editor = useSlateStatic()
  const path = ReactEditor.findPath(editor, element)

  const onDeleteImage = useCallback(() => {
    Transforms.removeNodes(editor, {
      at: path,
    })
    if (editor.onFileRemove && element.url) {
      editor.onFileRemove(element.url)
    }
  }, [path, editor, element.url])

  const onOpenImage = useCallback(() => {
    setImageViewDialogVisible(true)
  }, [])

  const onOpenSettings = useCallback(() => {
    setImageSettingsDialogVisible(true)
  }, [])

  const imageSettings = useCallback(() => {
    return getImageSettings({
      width: props.element['width'],
      caption: props.element['caption'],
      alt: props.element['alt'],
      url: props.element['url'],
    })
  }, [editor, props.element, path])

  const imageZoomUrl = useMemo(() => {
    const url = element.url
    if (url && url.includes('https://res.cloudinary.com')) {
      const urls = url.split('https')
      if (urls.length > 2) {
        const oldCloudinaryUrl = 'https' + urls[1]
        return url.replace(oldCloudinaryUrl, CLOUDINARY_ZOOM_URL)
      }
    }

    return url
  }, [element.url])

  const ImageView = useMemo(() => {
    return (
      <span
        onBlur={() => {
          updateCaptionUnderImage(editor, path, captionValue)
          setIsInputCaption(false)
        }}
      >
        <img
          src={element.url}
          alt={element.alt}
          style={{ width: '100%' }}
          className={styles.image}
        />
        {isInputCaption ? (
          <div className={styles.imageCaption}>
            <Input
              classes={{ root: styles.imageCaptionText }}
              autoFocus
              value={captionValue}
              onChange={(value) => {
                setCaptionValue(value)
              }}
            />
          </div>
        ) : element.caption ? (
          <div
            className={styles.imageCaption}
            contentEditable={!editor.disabled}
            onClick={() => {
              if (!editor.disabled) {
                setIsInputCaption(true)
              }
            }}
          >
            {element.caption}
          </div>
        ) : null}
      </span>
    )
  }, [element, isInputCaption, captionValue, path, editor.disabled])

  useEffect(() => {
    setCaptionValue(element.caption || '')
  }, [element.caption])

  return (
    <div style={{ marginLeft: props.style.marginLeft }} {...props.attributes}>
      <ModalDialog
        className={styles.imageViewPopup}
        show={imageViewDialogVisible}
        content={() => <img className={styles.imageViewPopup__img} src={imageZoomUrl} alt={''} />}
        size={'auto'}
        onClose={() => setImageViewDialogVisible(false)}
      />
      <ModalDialog
        title={'Image Settings'}
        className={styles.imageViewPopup}
        show={imageSettingsDialogVisible}
        content={ImageSettingsDialog}
        contentProps={{
          imageSetting: imageSettings(),
          onSave: (settings: TImageSettings) => {
            updateImage(editor, path, settings)
            updateCaptionUnderImage(editor, path, settings.caption)
            setImageSettingsDialogVisible(false)
          },
          onCancel: () => setImageSettingsDialogVisible(false),
        }}
        size={'auto'}
        onClose={() => setImageSettingsDialogVisible(false)}
      />
      <div
        contentEditable={false}
        // We use different styles for aligning text and images to maintain
        // a common style structure for aligning elements
        className={classNames(styles.root, {
          [styles.alignStart]: props.style.textAlign === 'left',
          [styles.alignEnd]: props.style.textAlign === 'right',
          [styles.alignCenter]: props.style.textAlign === 'center',
        })}
      >
        <TopPanelButtonsWrapper
          onDelete={editor.disabled ? undefined : onDeleteImage}
          onOpenSettings={editor.disabled ? undefined : onOpenSettings}
          onOpenView={onOpenImage}
          disabled={editor.disabled}
          width={element.width || '100%'}
        >
          {ImageView}
          {props.children}
        </TopPanelButtonsWrapper>
      </div>
    </div>
  )
}
